import { IdTokenResult, User } from 'firebase/auth'
import { signOutUser, singInWithLink, userStateListener } from '../firebase/firebaseAuth'
import { areReviewDraftsLocked, clearSessionData } from '../utils/sessionData'
import { createContext, useState, useEffect, ReactNode, Dispatch, SetStateAction } from 'react'
import axios from 'axios'
import Loading from '../componenents/Loading'

interface Props {
  children?: ReactNode
}

interface AuthContext {
  currentUser: User | null
  setCurrentUser: Dispatch<SetStateAction<User | null>>
  signOut: () => Promise<void>
  idToken: string | null
  getRefreshedToken: () => Promise<string | null>
  idTokenResult: IdTokenResult | null
}

export const AuthContext = createContext<AuthContext>(undefined as any)

export const AuthProvider = ({ children }: Props) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [idToken, setIdToken] = useState<string | null>(null)
  const [idTokenResult, setIdTokenResult] = useState<IdTokenResult | null>(null)

  useEffect(() => {
    singInWithLink()
  }, [])

  useEffect(() => {
    const unsubscribe = userStateListener((user) => {
      if (user) {
        setCurrentUser(user)
        user.getIdToken().then((token) => {
          setIdToken(token);
        });
        user.getIdTokenResult().then((tokenResult) => {
          setIdTokenResult(tokenResult);
        });
      } else if (currentUser && !areReviewDraftsLocked()) {
        // Signed out in another tab or the session was revoked.
        clearSessionData()
        window.location.replace('/')
      }
    })
    return unsubscribe
  }, [currentUser])

  const signOut = async () => {
    clearSessionData()
    try {
      await signOutUser()
    } finally {
      // A full reload drops every in-memory cache and the old token.
      window.location.replace('/')
    }
  }

  const getRefreshedToken = async () => {
    if (currentUser) {
      return await currentUser?.getIdToken(true)
    }
    return null
  }

  const value = {
    currentUser,
    setCurrentUser,
    signOut,
    idToken,
    getRefreshedToken,
    idTokenResult
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
